import { useState } from "react";
import { useAuth } from "../../context/useAuth";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { adminLogin } from "../../api/apiServices";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin:       "Admin",
  support:     "Support",
};

export default function AdminLogin() {
  const [email,        setEmail]        = useState("");
  const [password,     setPassword]     = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading,    setIsLoading]    = useState(false);

  const { login } = useAuth();
  const navigate  = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const data = await adminLogin(email, password);

      if (!data.token) {
        toast.error("Login failed — no token received");
        return;
      }

      const user = login(data.token, data.user);

      if (!user || user.role !== "admin") {
        toast.error("Access denied: admin accounts only");
        return;
      }

      const roleLabel = ROLE_LABELS[user.adminRole] || "Admin";
      toast.success(`Welcome, ${user.firstName || "Admin"} — ${roleLabel}`);
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Login failed. Check credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-4">
      <div className="w-full max-w-sm">

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#00C896] rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ boxShadow: "0 8px 24px rgba(0,200,150,0.3)" }}>
            <i className="ri-exchange-funds-line text-black text-2xl" />
          </div>
          <h1 className="text-white font-bold text-2xl">Easwallet Admin</h1>
          <p className="text-white/30 text-sm mt-1">Authorised personnel only</p>
        </div>

        {/* Form */}
        <div className="bg-[#161616] border border-white/8 rounded-2xl p-6 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5 font-semibold">
                Email Address
              </label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25 text-sm" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="admin@easwallet.app"
                  className="w-full bg-[#111] border border-white/10 rounded-xl pl-9 pr-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/50 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5 font-semibold">
                Password
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/25 text-sm" />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-[#111] border border-white/10 rounded-xl pl-9 pr-10 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/50 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/25 hover:text-white/60 transition-colors"
                >
                  <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-sm`} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-xl font-bold text-sm text-black transition-all active:scale-[0.98] disabled:opacity-60 mt-2 flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #00C896, #00a87d)", boxShadow: "0 4px 16px rgba(0,200,150,0.3)" }}
            >
              {isLoading ? (
                <><div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />Authenticating…</>
              ) : (
                <><i className="ri-login-box-line" />Sign in to Dashboard</>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-white/15 text-xs mt-6">
          &copy; {new Date().getFullYear()} Easwallet. All rights reserved.
        </p>
      </div>
    </div>
  );
}
